/* ============================================================
   VEYLA | currency display, borrowed from Vitals

   The store shows local currency (Morocco sees dirhams) through the Vitals
   app's Currency Converter: a client-side module that geo-detects the
   visitor and rewrites every price the theme printed inside
   <span class="money">, stamping what it did on the node:

     <span class="money" data-vitals-cc-usd="$39.95"
           data-vitals-cc-mad="393.95 dh"
           data-vitals-current-currency="MAD">393.95 dh</span>

   Our pages print prices as plain text and re-render them on every click,
   so Vitals can neither find them nor keep them converted. Fighting its
   DOM writer would mean flicker and races, so this module does the one
   robust thing instead: it PLANTS a hidden probe span, in Vitals' exact
   format, for every distinct amount the page can show, lets Vitals convert
   the probes, then reads the answers back. Exact parity with what the
   client's own widget would display, at zero API calls, and with their
   rate and their rounding, whatever they change them to.

   Amounts the probes cannot know in advance (a drawer subtotal after a
   quantity bump) are not computed here either: they get a probe of their
   own, planted the moment they are first asked for, and the page re-renders
   with Vitals' answer as soon as it lands. The client's own PDP never does
   arithmetic on a price, and neither do we. A learned rate survives only as
   a stopgap for the single frame between asking and being answered, so a
   new sum never blinks in dollars.

   If Vitals never converts (US visitor, blocked script, app gone), fmt()
   returns null and every caller keeps its own USD string.

   Public surface:
     window.VY_CC.fmt(cents)  converted display string, or null for USD
     window.VY_CC.currency()  active code ('MAD'), or null
     document event 'vy:currency'  fired whenever the mapping (re)loads,
                                   so pages re-render their prices

   Load order: after vy-offer.js (probe amounts come from the ladder),
   before vy-pdp.js. The drawer only calls fmt() at render time, so it can
   load first, and both surfaces listen for the event instead of holding a
   reference. DOM writes here are limited to one offscreen container.
   ============================================================ */
(() => {
  const O = window.VY_OFFER;
  if (!O || typeof document === 'undefined') return;

  const usd = c => '$' + (c / 100).toLocaleString('en-US',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /* ---------- every distinct amount the two surfaces can print ----------
     Tier totals in both modes, the RRP anchors, the savings lines, the
     per-tube figures, and the drawer's fixed catalogue (add-ons, shipping
     protection, the flat shipping line). Dynamic sums fall to the rule. */
  const cents = new Set([0, 295, 500, 1995, 2995, 3995, 4995, O.SERUM.rrpCents]);
  Object.keys(O.TIERS).forEach(k => {
    const t = O.TIERS[k];
    [true, false].forEach(sub => {
      const tot = O.tierTotal(k, sub);
      cents.add(tot);
      cents.add(t.compareCents);
      cents.add(t.compareCents - tot);
      cents.add(O.perTube(k, sub));
    });
  });

  /* ---------- the probes ---------- */
  const probes = new Map();   // cents -> span
  const bed = document.createElement('div');
  bed.id = 'vy-cc-probes';
  bed.setAttribute('aria-hidden', 'true');
  /* offscreen, not display:none: nothing in Vitals' docs promises it
     converts nodes that do not render */
  bed.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;';

  /* Vitals converts nodes that appear after its bundle ran (this whole bed
     is injected by script and it converts it), so a price we meet for the
     first time can simply be asked for. */
  const MAX_PROBES = 600;
  const ask = c => {
    if (probes.has(c) || probes.size >= MAX_PROBES || !(c >= 0)) return;
    const s = document.createElement('span');
    s.className = 'money';
    s.textContent = usd(c);
    probes.set(c, s);
    bed.appendChild(s);
  };
  [...cents].forEach(ask);

  const plant = () => document.body.appendChild(bed);
  document.body ? plant() : document.addEventListener('DOMContentLoaded', plant);

  /* ---------- harvest ---------- */
  const state = { active: false, code: null, map: new Map(), rate: null,
                  ending: null, prefix: '', suffix: '',
                  decimal: '.', group: ',', frac: 2, sig: null, ready: false };

  /* Vitals prints in the visitor's convention, not ours. Morocco reads
     "4,734.95 dh" but Brazil reads "R$ 2.462,95" and France "1 234,56 €",
     so the separators have to be read off the text, never assumed: guessing
     en-US turned a Brazilian cart total of R$ 220 into R$ 0.22.
     A space (any width) and an apostrophe are only ever grouping. A lone
     dot or comma is grouping when it repeats or when exactly three digits
     follow it (Vitals always prints a full fraction, so "2.462" can only be
     thousands); otherwise it is the decimal point. With both present, the
     rightmost one is the decimal point. */
  const SPACES = ' \u00a0\u202f\u2009\u2007\'';
  const NUMBER = new RegExp('\\d[\\d.,' + SPACES + ']*\\d|\\d');

  const parse = txt => {
    const m = txt.match(NUMBER);
    if (!m) return null;
    const raw = m[0];
    let group = null, decimal = null;
    const spaced = raw.split('').find(ch => SPACES.indexOf(ch) > -1);
    if (spaced) group = spaced;
    const body = raw.replace(new RegExp('[' + SPACES + ']', 'g'), '');
    const lastDot = body.lastIndexOf('.'), lastComma = body.lastIndexOf(',');
    if (lastDot > -1 && lastComma > -1) {
      decimal = lastDot > lastComma ? '.' : ',';
      group = decimal === '.' ? ',' : '.';
    } else if (lastDot > -1 || lastComma > -1) {
      const ch = lastDot > -1 ? '.' : ',';
      const at = Math.max(lastDot, lastComma);
      const repeats = body.split(ch).length - 1 > 1;
      if (repeats || body.length - at - 1 === 3) group = ch;
      else decimal = ch;
    }
    let digits = body;
    if (group === '.' || group === ',') digits = digits.split(group).join('');
    if (decimal) digits = digits.replace(decimal, '.');
    const num = parseFloat(digits);
    if (isNaN(num)) return null;
    return {
      num: num,
      group: group,
      decimal: decimal,
      frac: decimal ? body.length - Math.max(lastDot, lastComma) - 1 : 0,
      prefix: txt.slice(0, m.index),
      suffix: txt.slice(m.index + raw.length)
    };
  };

  const harvest = () => {
    const sample = probes.get([...cents].find(c => c > 0));
    const codeAttr = sample && sample.getAttribute('data-vitals-current-currency');
    if (!codeAttr) return false;                 // Vitals has not run yet
    if (codeAttr === 'USD') {                    // ran, decided no conversion
      if (state.active) { state.active = false; state.ready = true; fire(); }
      state.ready = true;
      return true;
    }
    const map = new Map();
    let rate = null, shape = null;
    const endings = new Set();
    const votes = { decimal: {}, group: {}, frac: {} };
    const vote = (k, v) => { if (v != null) votes[k][v] = (votes[k][v] || 0) + 1; };
    const winner = k => Object.keys(votes[k]).sort((a, b) => votes[k][b] - votes[k][a])[0];
    let biggest = 0;
    probes.forEach((span, c) => {
      /* a probe asked for a moment ago still reads in dollars; counting it
         would drag the rate and the separators back to en-US */
      if (span.getAttribute('data-vitals-current-currency') !== codeAttr) return;
      const p = parse(span.textContent);
      if (!p) return;
      map.set(c, span.textContent.trim());
      /* the largest probe carries the least rounding noise, so it sets the
         rate and the prefix/suffix; the separators are voted on across every
         probe, since a small one may show no grouping at all */
      if (c > biggest && p.num > 0) { biggest = c; rate = p.num / (c / 100); shape = p; }
      if (c > 0) endings.add(Math.round((p.num % 1) * 100));
      if (c > 0) { vote('decimal', p.decimal); vote('group', p.group); vote('frac', p.frac); }
    });
    if (!map.size || !rate || !shape) return false;
    state.active = true;
    state.code = codeAttr;
    state.map = map;
    state.rate = rate;
    state.ending = endings.size === 1 ? [...endings][0] / 100 : null;
    state.prefix = shape.prefix;
    state.suffix = shape.suffix;
    state.decimal = winner('decimal') || '';
    state.group = winner('group') || '';
    state.frac = state.decimal ? Number(winner('frac')) || 2 : 0;
    state.ready = true;
    /* asking for a probe is itself a mutation, so only tell the page when
       the answers actually changed, otherwise every ask costs a re-render */
    const sig = codeAttr + '|' + map.size + '|' + rate;
    if (sig !== state.sig) { state.sig = sig; fire(); }
    return true;
  };

  const fire = () => document.dispatchEvent(new CustomEvent('vy:currency'));

  /* Vitals lands whenever its bundle does, and the visitor can switch
     currency from its widget later: poll until the first answer, then a
     MutationObserver keeps the mapping honest forever after. */
  let polls = 0;
  const timer = setInterval(() => {
    if (harvest() || ++polls > 40) clearInterval(timer);
  }, 400);
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(() => { harvest(); })
      .observe(bed, { subtree: true, characterData: true, attributes: true, childList: true });
  }

  /* ---------- public ---------- */
  /* written the way the probes came back, not the way we would write it:
     1409.95 goes out as "1,409.95" for Morocco and "1.409,95" for Brazil */
  const write = n => {
    const fixed = Math.abs(n).toFixed(state.frac);
    const dot = fixed.indexOf('.');
    let whole = dot > -1 ? fixed.slice(0, dot) : fixed;
    const frac = dot > -1 ? fixed.slice(dot + 1) : '';
    if (state.group) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, state.group);
    return frac ? whole + state.decimal + frac : whole;
  };

  const fmt = c => {
    if (!state.active) return null;
    const exact = state.map.get(c);
    if (exact) return exact;
    /* an amount we have never shown before: ask Vitals for it and let the
       answer re-render the page. Everything below is the one-frame stopgap. */
    ask(c);
    const raw = (c / 100) * state.rate;
    let val;
    if (state.ending != null && raw >= 1) {
      /* their prices end .95 whatever the amount, so ours land on the same
         ending: nearest whole-plus-ending to the converted figure */
      val = Math.max(0, Math.round(raw - state.ending)) + state.ending;
    } else {
      val = Math.round(raw * 100) / 100;
    }
    return state.prefix + write(val) + state.suffix;
  };

  window.VY_CC = { fmt, currency: () => (state.active ? state.code : null) };
})();
